import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Listen,
    Method,
    Prop,
    State,
    Watch,
} from "@stencil/core";
import { ModalStatusTypes, ModalStatus } from "./types";

@Component({
    tag: "ux-cookie-layer-modal",
    styleUrl: "cookie-layer-modal.scss",
    shadow: false,
})
export class CookieLayerModal {
    @Element() domNode: HTMLUxCookieLayerModalElement;

    /** Set the name of the modal  */
    @Prop() name: string = "";

    /** The label of the close button */
    @Prop() closeButtonText: string = "";

    /** Disables the close button in the top corner */
    @Prop() noCloseButton: boolean = false;

    /** If present, uxModalClose event is emitted when escape key is pressed **/
    @Prop() closeOnEscKey: boolean = false;

    /** If present, uxModalClose event is emitted when the backdrop is clicked **/
    @Prop() closeOnBackdropClick: boolean = false;

    /** reloads the page when the modal is closed */
    @Prop() reloadOnClose: boolean = false;

    /** At which vertical position should the maodal be docked **/
    @Prop() stickyPosition: "center" | "bottom" | "top" = "center";

    /** Boolean to indicate if or not the modal is currently opened */
    @Prop({ mutable: true, reflect: true }) opened: boolean = false;

    /** The modal transition status */
    @State() status: ModalStatus = ModalStatusTypes.CLOSED;

    /** Emitted when the modal is closed  */
    @Event({ eventName: "uxModalClose", bubbles: true, cancelable: true })
    modalClosed: EventEmitter<string>;

    /** Emitted when the modal is opened  */
    @Event({ eventName: "uxModalOpen", bubbles: true, cancelable: true })
    modalOpened: EventEmitter<string>;

    private transitionTimeout: any | null = null;

    /** Triggers modal closing */
    @Method()
    async triggerClose(): Promise<void> {
        const evt = this.modalClosed.emit(this.name);
        if (!evt.defaultPrevented) {
            this.opened = false;
            if (this.reloadOnClose) {
                window.setTimeout(() => window.location.reload(), 250);
            }
        }
    }

    /** Triggers modal opening */
    @Method()
    async triggerOpen(): Promise<void> {
        const evt = this.modalOpened.emit(this.name);
        if (!evt.defaultPrevented) {
            this.opened = true;
        }
    }

    /** Listen for opened state  */
    @Watch("opened")
    setModalStatus() {
        if (this.transitionTimeout !== null) {
            clearTimeout(this.transitionTimeout);
        }

        if (!this.opened) {
            if (this.status === ModalStatusTypes.CLOSED) {
                return;
            }
            this.status = ModalStatusTypes.CLOSING;
            this.transitionTimeout = setTimeout(() => {
                this.status = ModalStatusTypes.CLOSED;
            }, 350);
            return;
        }

        if (this.status === ModalStatusTypes.OPENED) {
            return;
        }
        this.showModal();
    }

    private showModal(): void {
        this.status = ModalStatusTypes.OPENING;
        this.transitionTimeout = setTimeout(() => {
            this.status = ModalStatusTypes.OPENED;
        }, 100);
    }

    /** Listen for escape key  */
    @Listen("keydown", { target: "window" })
    handleKeyDown(event: KeyboardEvent) {
        if (this.closeOnEscKey && event.key === "Escape") {
            this.triggerClose();
        }
    }

    private triggerElementClickHandler = (e: MouseEvent) => {
        const targetElement = (e.target as HTMLElement).closest(
            "[data-trigger-modal]"
        );
        if (targetElement === null) {
            return;
        }
        const triggers: string[][] = (
            targetElement.getAttribute("data-trigger-modal") || ""
        )
            .split(";")
            .map((string) => string.split(","));
        triggers.forEach((trigger) => {
            if (trigger.length > 1) {
                switch (trigger[0].valueOf()) {
                    case "open":
                        if (trigger[1] === this.name) {
                            this.triggerOpen();
                        }
                        break;
                    case "close":
                        if (
                            trigger[1] === undefined ||
                            trigger[1] === this.name
                        ) {
                            this.triggerClose();
                        }
                        break;
                    default:
                        break;
                }
            }
        });
    };

    componentWillLoad() {
        this.setModalStatus();
    }

    componentDidLoad() {
        document.addEventListener("click", this.triggerElementClickHandler);
    }

    disconnectedCallback() {
        document.removeEventListener("click", this.triggerElementClickHandler);
    }

    render() {
        const handleClickClose = () => {
            this.triggerClose();
        };
        return (
            <Host
                class={{
                    ["ux-cookie-layer-modal"]: true,
                    [`ux-cookie-layer-modal--${this.status}`]: true,
                    [`ux-cookie-layer-modal--sticky-${this.stickyPosition}`]: true,
                }}
            >
                <div
                    class="ux-cookie-layer-modal__backdrop"
                    onClick={
                        this.closeOnBackdropClick ? handleClickClose : null
                    }
                />
                <div
                    class="ux-cookie-layer-modal__outer"
                    role="dialog"
                    aria-modal={true}
                >
                    <div class="ux-cookie-layer-modal__inner">
                        <div class="ux-cookie-layer-modal__head">
                            {!this.noCloseButton && (
                                <button
                                    type="button"
                                    class="ux-cookie-layer-modal__close"
                                    aria-label={this.closeButtonText}
                                    title={this.closeButtonText}
                                    onClick={handleClickClose}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 29.7 29.7"
                                        class="ux-cookie-layer-modal__close-svg"
                                    >
                                        <polygon points="29.7 1.41 28.28 0 14.85 13.44 1.41 0 0 1.41 13.44 14.85 0 28.28 1.41 29.7 14.85 16.26 28.28 29.7 29.7 28.28 16.26 14.85 29.7 1.41" />
                                    </svg>
                                </button>
                            )}
                        </div>
                        <div class="ux-cookie-layer-modal__slot">
                            <slot />
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
